import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addTab as addShipmentTab } from '../../../../redux/slices/shipmentSlice';
import { addTab as addOrderTab } from '../../../../redux/slices/ordersSlice';
import { addTab as addInvoiceTab, setActiveTab as setActiveInvoiceTab } from '../../../../redux/slices/invoiceSlice';

/**
 * Custom hook for navigating to entity detail views from AI chatbot
 * Handles opening shipments, invoices, and orders in tabs
 */
export const useEntityNavigation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { defaultTabs = [], dynamicTabs = [] } = useSelector((state) => state.invoices || {});

  /**
   * Opens an entity in a detail view tab
   * @param {Object} entityData - The entity data from chatbot response
   * @param {string} entityType - Type of entity ("shipment", "invoice", "order", or with "-list" suffix)
   */
  const openEntity = (entityData, entityType) => {
    if (!entityData) {
      console.warn('Cannot navigate: entity data is missing');
      return;
    }

    // Normalize response type (strip -list suffix if present)
    const baseType = entityType.replace('-list', '');

    switch (baseType) {
      case 'shipment': {
        const { shipmentNumber, officeCode } = entityData;

        if (!shipmentNumber) {
          console.warn('Cannot navigate to shipment: shipmentNumber is missing', entityData);
          return;
        }

        dispatch(addShipmentTab({
          id: `view-${shipmentNumber}-${Date.now()}`,
          name: shipmentNumber,
          contentType: 'shipmentView',
          shipmentNumber,
          shipmentOfficeCode: officeCode,
          isDynamic: true,
          createdAt: Date.now(),
        }));
        navigate('/dashboard/shipments');
        break;
      }

      case 'invoice': {
        const { invoiceNumber, uniqueId } = entityData;

        if (!invoiceNumber && !uniqueId) {
          console.warn('Cannot navigate to invoice: invoiceNumber/uniqueId is missing', entityData);
          return;
        }

        const identifier = invoiceNumber || uniqueId;
        const tabKey = `view-${identifier}-${Date.now()}`;

        // Comprehensive data normalization to ensure compatibility with invoice view
        const normalizedShipments = (entityData.shipments || []).map(shipment => ({
          ...shipment,
          // Ensure charges is always an array with proper structure
          charges: Array.isArray(shipment.charges)
            ? shipment.charges.map(charge => ({
                ...charge,
                discrepancyStatus: charge.discrepancyStatus || charge.chargeAuditStatus || "No Discrepancy"
              }))
            : [],
          // Ensure shipToInfo is always present
          shipToInfo: shipment.shipToInfo || shipment.shipTos || []
        }));

        // Create normalized invoice data with all required fields
        const normalizedInvoiceData = {
          ...entityData,
          shipments: normalizedShipments,
          // Add fallback for top-level charges if needed
          charges: Array.isArray(entityData.charges)
            ? entityData.charges.map(charge => ({
                ...charge,
                discrepancyStatus: charge.discrepancyStatus || charge.chargeAuditStatus || "No Discrepancy"
              }))
            : []
        };

        dispatch(addInvoiceTab({
          id: tabKey,
          name: identifier,
          type: 'edit',
          data: normalizedInvoiceData,
          shipments: normalizedShipments,
          isDynamic: true,
          createdAt: Date.now(),
        }));
        // Invoice tabs require explicit activation
        dispatch(setActiveInvoiceTab({ index: defaultTabs.length + dynamicTabs.length }));
        navigate('/dashboard/invoices');
        break;
      }

      case 'order': {
        const { orderNumber, id, officeCode } = entityData;

        if (!orderNumber && !id) {
          console.warn('Cannot navigate to order: orderNumber/id is missing', entityData);
          return;
        }

        const identifier = orderNumber || id;
        dispatch(addOrderTab({
          id: `view-${identifier}-${Date.now()}`,
          name: identifier,
          contentType: 'orderView',
          orderNumber: identifier,
          orderOfficeCode: officeCode,
          isDynamic: true,
          createdAt: Date.now(),
        }));
        navigate('/dashboard/orders');
        break;
      }

      default:
        console.warn(`Unknown entity type: ${entityType}`);
    }
  };

  return { openEntity };
};
